import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useClerkAuth';
import TopNavBar from '@/components/TopNavBar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress as ProgressBar } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3,
  BookOpen,
  CheckCircle,
  Clock,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Calendar,
  ArrowLeft,
  Brain,
  Languages,
  BookMarked,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// Types for progress data
interface QuestionCatalogProgress {
  catalogId: string;
  catalogTitle: string;
  completedQuestions: number;
  totalQuestions: number;
  completionPercentage: number;
  lastPracticedAt: string;
  selectedState?: string;
}

interface ProgressStats {
  totalQuestionsAnswered: number;
  totalQuestionCatalogs: number;
  completedCatalogs: number;
  averageScore: number;
  currentStreak: number;
  longestStreak: number;
  totalStudyTime: string;
  articlesRead: number;
  wordsLearned: number;
  catalogProgress: QuestionCatalogProgress[];
}

const Progress = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<ProgressStats>({
    totalQuestionsAnswered: 0,
    totalQuestionCatalogs: 0,
    completedCatalogs: 0,
    averageScore: 0,
    currentStreak: 0,
    longestStreak: 0,
    totalStudyTime: '0h 0m',
    articlesRead: 0,
    wordsLearned: 0,
    catalogProgress: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadProgressData = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      // Get all question catalogs progress for the user
      const { data: progressData, error } = await supabase
        .from('user_practice_progress')
        .select(
          `
          *,
          question_catalogs (
            name
          )
        `
        )
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching progress data:', error);
        return;
      }

      // Process the progress data
      const catalogProgress: QuestionCatalogProgress[] = [];
      let totalAnswered = 0;
      let completedCount = 0;

      progressData?.forEach(progress => {
        const completedQuestions = progress.completed_questions
          ? progress.completed_questions.split(',').filter(Boolean).length
          : 0;

        const percentage =
          progress.total_questions > 0
            ? Math.round((completedQuestions / progress.total_questions) * 100)
            : 0;

        if (percentage >= 100) completedCount++;
        totalAnswered += completedQuestions;

        catalogProgress.push({
          catalogId: progress.catalog_id,
          catalogTitle: progress.question_catalogs?.name || 'Unknown Catalog',
          completedQuestions,
          totalQuestions: progress.total_questions || 0,
          completionPercentage: percentage,
          lastPracticedAt:
            progress.last_practiced_at || new Date().toISOString(),
          selectedState: progress.selected_state || undefined,
        });
      });

      // Calculate streaks and other stats
      const daysActive = calculateActiveStreak(progressData);

      setStats({
        totalQuestionsAnswered: totalAnswered,
        totalQuestionCatalogs: catalogProgress.length,
        completedCatalogs: completedCount,
        averageScore:
          catalogProgress.length > 0
            ? Math.round(
                catalogProgress.reduce(
                  (sum, p) => sum + p.completionPercentage,
                  0
                ) / catalogProgress.length
              )
            : 0,
        currentStreak: daysActive.current,
        longestStreak: daysActive.longest,
        totalStudyTime: calculateStudyTime(progressData),
        articlesRead: Math.floor(totalAnswered / 10), // Mock calculation
        wordsLearned: totalAnswered * 5, // Mock calculation (5 words per question)
        catalogProgress,
      });
    } catch (error) {
      console.error('Error loading progress data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadProgressData();
    }
  }, [user, loadProgressData]);

  // Helper function to calculate active streaks
  const calculateActiveStreak = (
    progressData: Array<{
      catalog_id: string;
      completed_questions: string | null;
      created_at: string | null;
      current_question_number: number;
      id: string;
      last_practiced_at: string | null;
      selected_state: string | null;
      total_questions: number;
      updated_at: string | null;
      user_id: string;
      question_catalogs: { name: string };
    }>
  ) => {
    if (!progressData || progressData.length === 0) {
      return { current: 0, longest: 0 };
    }

    // Get all practice dates
    const practiceDates = progressData
      .filter(p => p.last_practiced_at)
      .map(p => new Date(p.last_practiced_at!).toDateString())
      .filter((date, index, self) => self.indexOf(date) === index)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    let current = 0;
    let longest = 0;
    let tempStreak = 0;

    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    // Calculate current streak
    if (practiceDates.includes(today) || practiceDates.includes(yesterday)) {
      current = 1;
      for (let i = 1; i < practiceDates.length; i++) {
        const prevDate = new Date(practiceDates[i - 1]);
        const currDate = new Date(practiceDates[i]);
        const dayDiff =
          (prevDate.getTime() - currDate.getTime()) / (1000 * 60 * 60 * 24);

        if (dayDiff === 1) {
          current++;
        } else {
          break;
        }
      }
    }

    // Calculate longest streak
    tempStreak = 1;
    for (let i = 1; i < practiceDates.length; i++) {
      const prevDate = new Date(practiceDates[i - 1]);
      const currDate = new Date(practiceDates[i]);
      const dayDiff =
        (prevDate.getTime() - currDate.getTime()) / (1000 * 60 * 60 * 24);

      if (dayDiff === 1) {
        tempStreak++;
        longest = Math.max(longest, tempStreak);
      } else {
        tempStreak = 1;
      }
    }

    longest = Math.max(longest, tempStreak, current);

    return { current, longest };
  };

  // Helper function to calculate study time
  const calculateStudyTime = (
    progressData: Array<{
      catalog_id: string;
      completed_questions: string | null;
      created_at: string | null;
      current_question_number: number;
      id: string;
      last_practiced_at: string | null;
      selected_state: string | null;
      total_questions: number;
      updated_at: string | null;
      user_id: string;
      question_catalogs: { name: string };
    }>
  ) => {
    if (!progressData || progressData.length === 0) return '0h 0m';

    // Mock calculation: assume 30 seconds per question
    const totalQuestions = progressData.reduce((sum, p) => {
      const completed = p.completed_questions
        ? p.completed_questions.split(',').filter(Boolean).length
        : 0;
      return sum + completed;
    }, 0);

    const totalMinutes = Math.floor(totalQuestions * 0.5); // 30 seconds per question
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  const getStreakEmoji = (streak: number) => {
    if (streak >= 30) return '🔥';
    if (streak >= 14) return '⚡';
    if (streak >= 7) return '✨';
    if (streak >= 3) return '💫';
    return '🌱';
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 70) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  return (
    <>
      <TopNavBar
        currentPage="progress"
        onNavigateToAdmin={() => navigate('/?view=admin')}
        onNavigateToArticles={() => navigate('/')}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 p-6">
        <div className="container max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="bg-white/10 border-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('progress.backToDashboard')}
              </Button>
            </div>

            <div className="bg-gradient-hero rounded-lg p-6 text-primary-foreground mb-6">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="h-8 w-8" />
                <h1 className="text-3xl font-bold">{t('progress.title')}</h1>
              </div>
              <p className="text-primary-foreground/90">
                {t('progress.subtitle')}
              </p>
            </div>
          </div>

          {/* Quick Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('progress.questionsAnswered')}
                    </p>
                    <p className="text-2xl font-bold">
                      {isLoading ? '...' : stats.totalQuestionsAnswered}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('progress.totalAnswered')}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('progress.currentStreak')}
                    </p>
                    <p className="text-2xl font-bold flex items-center gap-1">
                      {isLoading ? '...' : stats.currentStreak}
                      <span className="text-lg">
                        {getStreakEmoji(stats.currentStreak)}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('progress.daysActive')}
                    </p>
                  </div>
                  <Star className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('progress.studyTime')}
                    </p>
                    <p className="text-2xl font-bold">
                      {isLoading ? '...' : stats.totalStudyTime}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('progress.totalTime')}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('progress.avgProgress')}
                    </p>
                    <p className="text-2xl font-bold">
                      {isLoading ? '...' : `${stats.averageScore}%`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('progress.acrossCatalogs')}
                    </p>
                  </div>
                  <Target className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Stats Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Achievement Stats */}
            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  {t('progress.achievements')}
                </CardTitle>
                <CardDescription>
                  {t('progress.achievementsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookMarked className="h-4 w-4 text-green-600" />
                      <span className="text-sm">
                        {t('progress.completedCatalogs')}
                      </span>
                    </div>
                    <Badge variant="secondary">
                      {stats.completedCatalogs}/{stats.totalQuestionCatalogs}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                      <span className="text-sm">
                        {t('progress.longestStreak')}
                      </span>
                    </div>
                    <Badge variant="secondary">
                      {stats.longestStreak} {t('progress.daysActive')}{' '}
                      {getStreakEmoji(stats.longestStreak)}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Languages className="h-4 w-4 text-purple-600" />
                      <span className="text-sm">
                        {t('progress.wordsLearned')}
                      </span>
                    </div>
                    <Badge variant="secondary">{stats.wordsLearned}</Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-orange-600" />
                      <span className="text-sm">
                        {t('progress.articlesRead')}
                      </span>
                    </div>
                    <Badge variant="secondary">{stats.articlesRead}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Learning Calendar */}
            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  {t('dashboard.recentActivity')}
                </CardTitle>
                <CardDescription>
                  {t('progress.activityCalendar')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="mb-2">{t('progress.activityCalendar')}</p>
                  <p className="text-sm">{t('progress.visualTracking')}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Progress by Catalog */}
          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookMarked className="h-5 w-5" />
                {t('progress.catalogProgress')}
              </CardTitle>
              <CardDescription>
                {t('progress.catalogProgressDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-learning-primary mx-auto mb-4" />
                  {t('progress.loadingProgress')}
                </div>
              ) : stats.catalogProgress.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="mb-4">{t('progress.noPracticeProgress')}</p>
                  <Button onClick={() => navigate('/questions')}>
                    {t('progress.startPracticing')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {stats.catalogProgress.map(catalog => (
                    <div
                      key={`${catalog.catalogId}-${catalog.selectedState || 'all'}`}
                      className="space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">
                            {catalog.catalogTitle}
                          </h4>
                          {catalog.selectedState && (
                            <p className="text-sm text-muted-foreground">
                              {t('progress.state')} {catalog.selectedState}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className={getProgressColor(
                                catalog.completionPercentage
                              )}
                            >
                              {catalog.completionPercentage}%
                            </Badge>
                            {catalog.completionPercentage >= 100 && (
                              <Trophy className="h-4 w-4 text-yellow-500" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {catalog.completedQuestions} /{' '}
                            {catalog.totalQuestions} questions
                          </p>
                        </div>
                      </div>

                      <ProgressBar
                        value={catalog.completionPercentage}
                        className="h-2"
                      />

                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {t('progress.lastPracticed')}{' '}
                          {new Date(
                            catalog.lastPracticedAt
                          ).toLocaleDateString()}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/questions/${catalog.catalogId}/practice${catalog.selectedState ? `?state=${catalog.selectedState}` : ''}`
                            )
                          }
                        >
                          {t('progress.continuePractice')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Progress;
